import React, { useState, useEffect, useCallback } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { getmobilebyemail, sharedCompany } from '../../../redux/actions/company';
import './css.css';
import ManageUserRoleForm from 'components/navbar/ManageUsers/ManageUserRoleForm';
import { useGlobal } from '../../../contexts';
import { useColorMode } from '@chakra-ui/react';

const validationSchema = Yup.object().shape({
   email: Yup.string().email('Invalid email format').required('Email is required'),
   mobile: Yup.string()
      .matches(/^[0-9]{10}$/, 'Invalid mobile format')
      .required('Mobile is required'),
});

const SharedCompForm = ({ isOpen, onClose, currentRow, selectedSharedComp, isEditMode = false }) => {
   const [errors, setErrors] = useState({});
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
   const [disablMobile, setDisablMobile] = useState(false);
   const dispatch = useDispatch();
   const { companyList } = useGlobal();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [row, setRow] = useState({
      email: '',
      mobile: '',
   });

   const [processedData, setProcessedData] = useState(null);
   const [showUserRoleModal, setShowUserRoleModal] = useState(false);
   const [toast, setToast] = useState({ show: false, title: '', message: '', type: '' });

   const clearFormData = useCallback(() => {
      setRow({
         email: '',
         mobile: '',
      });
      setErrors({});
      setProcessedData(null);
   }, []);

   const handleOnClose = useCallback(() => {
      clearFormData();
      onClose();
      setDisablMobile(false);
   }, [clearFormData, onClose]);

   // Handle ESC key press
   useEffect(() => {
      if (!isOpen || showUserRoleModal) return;

      const handleEscKey = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleOnClose();
         }
      };

      window.addEventListener('keydown', handleEscKey, true);
      document.addEventListener('keydown', handleEscKey, true);
      document.body.addEventListener('keydown', handleEscKey, true);

      return () => {
         window.removeEventListener('keydown', handleEscKey, true);
         document.removeEventListener('keydown', handleEscKey, true);
         document.body.removeEventListener('keydown', handleEscKey, true);
      };
   }, [isOpen, showUserRoleModal, handleOnClose]);

   const showToast = (title, message, type) => {
      setToast({ show: true, title, message, type });
      setTimeout(() => setToast({ show: false, title: '', message: '', type: '' }), 3000);
   };

   useEffect(() => {
      const timerId = setTimeout(() => {
         setDebouncedSearchQuery(row.email);
      }, 500);

      return () => clearTimeout(timerId);
   }, [row.email]);

   useEffect(() => {
      if (!debouncedSearchQuery) return;

      const fetchData = async () => {
         try {
            setDisablMobile(false);
            const res = await dispatch(getmobilebyemail({ search: debouncedSearchQuery }));
            if (res && res?.mobile) {
               console.log('res', res);
               handleInputChange('mobile', res?.mobile);
               setDisablMobile(true);
            }
         } catch (error) {
            console.error('Error fetching mobile by email:', error);
         }
      };

      fetchData();
   }, [debouncedSearchQuery, dispatch]);

   const handleInputChange = (field, value) => {
      setRow((prev) => ({ ...prev, [field]: value }));

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const validateStep = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const prepareUserRights = () => {
      const initialUserRights = {
         dashboard: { add: false, edit: false, deleteRole: false, print: false, view: false },
         // cash: { add: false, edit: false, deleteRole: false, print: false, view: false },
         // bank: { add: false, edit: false, deleteRole: false, print: false, view: false },
         journalvouchers: { add: false, edit: false, deleteRole: false, print: false, view: false },
         reports: { add: false, edit: false, deleteRole: false, print: false, view: false },
         Profile: { add: false, edit: false, deleteRole: false, print: false, view: false },
         companyprofile: { add: false, edit: false, deleteRole: false, print: false, view: false },
         cashReceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         cashPayment: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankReceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankPayment: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Masters
         customer: { add: false, edit: false, deleteRole: false, print: false, view: false },
         vendor: { add: false, edit: false, deleteRole: false, print: false, view: false },
         AllAccount: { add: false, edit: false, deleteRole: false, print: false, view: false },
         item: { add: false, edit: false, deleteRole: false, print: false, view: false },
         category: { add: false, edit: false, deleteRole: false, print: false, view: false },
         brand: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salerepresentative: { add: false, edit: false, deleteRole: false, print: false, view: false },
         itemGroup: { add: false, edit: false, deleteRole: false, print: false, view: false },
         warehouse: { add: false, edit: false, deleteRole: false, print: false, view: false },
         unit: { add: false, edit: false, deleteRole: false, print: false, view: false },
         screen: { add: false, edit: false, deleteRole: false, print: false, view: false },
         daybook: { add: false, edit: false, deleteRole: false, print: false, view: false },
         billtterm: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankbranch: { add: false, edit: false, deleteRole: false, print: false, view: false },
         transport: { add: false, edit: false, deleteRole: false, print: false, view: false },
         driver: { add: false, edit: false, deleteRole: false, print: false, view: false },
         vehicle: { add: false, edit: false, deleteRole: false, print: false, view: false },
         color: { add: false, edit: false, deleteRole: false, print: false, view: false },
         style: { add: false, edit: false, deleteRole: false, print: false, view: false },
         size: { add: false, edit: false, deleteRole: false, print: false, view: false },
         hsn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         countries: { add: false, edit: false, deleteRole: false, print: false, view: false },
         states: { add: false, edit: false, deleteRole: false, print: false, view: false },
         city: { add: false, edit: false, deleteRole: false, print: false, view: false },
         Area: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Sale
         saleinvoice: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salereturn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         saleorder: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salechallan: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Purchase
         purchaseinvoice: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchasereturn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchaseorder: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchasechallan: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Inventory
         inventory: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventoryissue: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventoryreceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventorystocktransfer: { add: false, edit: false, deleteRole: false, print: false, view: false },
      };

      const data = {
         ...row,
         userRights: [
            {
               ...initialUserRights,
               companyId: selectedSharedComp ? parseInt(selectedSharedComp, 10) : null,
            },
         ],
      };

      return data;
   };

   const onFormSubmit = async () => {
      try {
         const isValid = await validateStep();
         if (!isValid) {
            showToast('Validation Error', 'Please correct the form errors.', 'error');
            return;
         }

         const data = prepareUserRights();
         setProcessedData(data);
         setShowUserRoleModal(true);
      } catch (error) {
         showToast('Error', error.message || 'An error occurred while preparing the data.', 'error');
      }
   };

   const handleManageUserRoleClose = () => {
      setShowUserRoleModal(false);
      clearFormData();
      onClose();
   };

   const handleSaveUserRights = async (updatedData) => {
      try {
         const finalData = {
            ...processedData,
            userRights: updatedData.userRights || processedData.userRights,
         };

         await dispatch(sharedCompany(finalData));
         showToast('Success', 'Company shared successfully', 'success');

         handleManageUserRoleClose();
      } catch (error) {
         showToast('Error', error.message || 'An error occurred while saving the data.', 'error');
      }
   };

   useEffect(() => {
      if (isOpen && isEditMode && currentRow) {
         setRow((prev) => ({
            ...prev,
            email: currentRow?.email,
            mobile: currentRow?.mobile,
         }));
      }
   }, [currentRow, isEditMode, isOpen]);

   if (!isOpen) return null;

   return (
      <>
         {toast.show && (
            <div className="fixed top-4 right-4 z-[99999] animate-fade-in">
               <div
                  className={`rounded-xl shadow-2xl p-4 min-w-[320px] backdrop-blur-sm transition-all ${toast.type === 'success'
                        ? isDark
                           ? 'bg-emerald-900/90 border border-emerald-700/50'
                           : 'bg-emerald-50 border border-emerald-200'
                        : toast.type === 'error'
                           ? isDark
                              ? 'bg-red-900/90 border border-red-700/50'
                              : 'bg-red-50 border border-red-200'
                           : isDark
                              ? 'bg-blue-900/90 border border-blue-700/50'
                              : 'bg-blue-50 border border-blue-200'
                     }`}>
                  <div className="flex items-start gap-3">
                     <div className="flex-shrink-0">
                        {toast.type === 'success' && (
                           <svg
                              className={`h-6 w-6 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`}
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                 fillRule="evenodd"
                                 d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                 clipRule="evenodd"
                              />
                           </svg>
                        )}
                        {toast.type === 'error' && (
                           <svg
                              className={`h-6 w-6 ${isDark ? 'text-red-400' : 'text-red-500'}`}
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                 fillRule="evenodd"
                                 d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                 clipRule="evenodd"
                              />
                           </svg>
                        )}
                     </div>
                     <div className="flex-1">
                        <h3
                           className={`text-sm font-semibold ${toast.type === 'success'
                                 ? isDark
                                    ? 'text-emerald-200'
                                    : 'text-emerald-800'
                                 : toast.type === 'error'
                                    ? isDark
                                       ? 'text-red-200'
                                       : 'text-red-800'
                                    : isDark
                                       ? 'text-blue-200'
                                       : 'text-blue-800'
                              }`}>
                           {toast.title}
                        </h3>
                        <p
                           className={`mt-1 text-sm ${toast.type === 'success'
                                 ? isDark
                                    ? 'text-emerald-300'
                                    : 'text-emerald-700'
                                 : toast.type === 'error'
                                    ? isDark
                                       ? 'text-red-300'
                                       : 'text-red-700'
                                    : isDark
                                       ? 'text-blue-300'
                                       : 'text-blue-700'
                              }`}>
                           {toast.message}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {!showUserRoleModal && (
            <div className="fixed inset-0 z-[99998] flex items-center justify-center">
               <div
                  className={`absolute inset-0 transition-all ${isDark ? 'bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95' : 'bg-black/50'
                     }`}
                  onClick={handleOnClose}
               />

               <div
                  className={`relative rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-5 z-[9999] transition-all ${isDark
                        ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700/50'
                        : 'bg-white border-2 border-gray-100'
                     }`}>
                  <div
                     className={`flex items-center justify-between mb-4 pb-4 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'
                        }`}>
                     <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        Share Company
                     </h2>
                     <button
                        onClick={handleOnClose}
                        className={`rounded-full p-1 transition-colors ${isDark
                              ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                           }`}>
                        <svg
                           className="w-6 h-6"
                           fill="none"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           viewBox="0 0 24 24"
                           stroke="currentColor">
                           <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                     </button>
                  </div>

                  <div className="mb-6">
                     <div className="grid gap-1">
                        <div className="col-span-4">
                           <div className="grid gap-2">
                              <div className="seperator-width">
                                 <div className="flex flex-col">
                                    <label
                                       htmlFor="email"
                                       className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                                          }`}>
                                       Email <span className={isDark ? 'text-red-400' : 'text-red-500'}>*</span>
                                    </label>
                                    <input
                                       id="email"
                                       type="email"
                                       value={row.email}
                                       onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                                       placeholder="Enter email address"
                                       className={`w-full px-4 py-2 rounded-md transition-all focus:outline-none focus:ring-2 ${errors.email
                                             ? isDark
                                                ? 'bg-slate-800 border-2 border-red-500/50 text-white placeholder-red-400/50 focus:border-red-500 focus:ring-red-500/20'
                                                : 'bg-white border-2 border-red-300 text-gray-900 placeholder-red-400 focus:border-red-500 focus:ring-red-500/20'
                                             : isDark
                                                ? 'bg-slate-800 border-2 border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20'
                                                : 'bg-white border-2 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-green-500/20'
                                          }`}
                                       required
                                    />
                                    {errors.email && (
                                       <span className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                                          {errors.email}
                                       </span>
                                    )}
                                 </div>
                                 <div className="flex flex-col mt-4">
                                    <label
                                       htmlFor="mobile"
                                       className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                                          }`}>
                                       Mobile <span className={isDark ? 'text-red-400' : 'text-red-500'}>*</span>
                                    </label>
                                    <input
                                       id="mobile"
                                       type="number"
                                       value={row.mobile}
                                       onChange={(e) => handleInputChange('mobile', e.target.value)}
                                       placeholder="Enter mobile address"
                                       disabled={disablMobile === true}
                                       className={`w-full px-4 py-2 rounded-md transition-all focus:outline-none focus:ring-2 ${disablMobile ? 'opacity-60 cursor-not-allowed' : ''} ${errors.mobile
                                             ? isDark
                                                ? 'bg-slate-800 border-2 border-red-500/50 text-white placeholder-red-400/50 focus:border-red-500 focus:ring-red-500/20'
                                                : 'bg-white border-2 border-red-300 text-gray-900 placeholder-red-400 focus:border-red-500 focus:ring-red-500/20'
                                             : isDark
                                                ? 'bg-slate-800 border-2 border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20'
                                                : 'bg-white border-2 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-green-500/20'
                                          }`}
                                       required
                                    />
                                    {errors.mobile && (
                                       <span className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                                          {errors.mobile}
                                       </span>
                                    )}
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="flex justify-end gap-3">
                     <button
                        onClick={onFormSubmit}
                        className={`px-6 py-2 rounded-md transition-colors font-medium shadow-sm ${isDark
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                           }`}>
                        Next
                     </button>
                     <button
                        onClick={handleOnClose}
                        className={`px-6 py-2 rounded-md transition-colors font-medium ${isDark
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border border-slate-600'
                              : 'bg-transparent text-gray-700 hover:bg-gray-100'
                           }`}>
                        Cancel
                     </button>
                  </div>
               </div>
            </div>
         )}

         {showUserRoleModal && processedData && (
            <ManageUserRoleForm
               isOpen={showUserRoleModal}
               onClose={handleManageUserRoleClose}
               selectedMember={{ email: row.email, mobile: row.mobile }}
               companyList={companyList}
               selectedSharedCompId={selectedSharedComp}
               processedData={processedData}
               onSaveUserRights={handleSaveUserRights}
            />
         )}
      </>
   );
};

export default SharedCompForm;
